import { useEffect, useState } from "react";
import { ReactComponent as FilledTimeSvg } from "assets/Svgs/FilledTime.svg";
import { ReactComponent as TimeLapseSvg } from "assets/Svgs/TimeLapse.svg";
import { ReactComponent as VectorSvg } from "assets/Svgs/Vector.svg";
import { ReactComponent as SpaceDashboardSvg } from "assets/Svgs/SpaceDashboard.svg";
import { ReactComponent as OvenGenSvg } from "assets/Svgs/OvenGen.svg";
import { ReactComponent as ProductFilter } from "assets/Svgs/ProductFilter.svg";
import { TextIcon } from "247factorynet-packages";
import moment from "moment";
import ReportsTable from "./ReportsTable";
import { Button, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { EmptyState } from "components/EmptyState";
import { ReactComponent as ChartSvg } from "assets/Svgs/Chart.svg";
import { ReactComponent as EditSvg } from "assets/Svgs/Edit.svg";
import { useLocation } from "react-router-dom";
import i18n from "i18n/i18n";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as RoundedEditSvg } from "assets/Svgs/RoundedEdit.svg";
import { ReactComponent as RoundedbackSvg } from "assets/Svgs/RoundedBack.svg";
import { ReactComponent as RoundedDeleteSvg } from "assets/Svgs/RoundedDeleteSvg.svg";
import { ReactComponent as SystemUpdateSvg } from "assets/Svgs/SystemUpdate.svg";
import { ReactComponent as RepititionInterval } from "assets/Svgs/RepititionInterval.svg";

import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { useBoundStore } from "store/store";
import { ReactComponent as FlickCursorSvg } from "assets/Svgs/FlickCursor.svg";
import { getSvgByReportConfigType } from "utils/helper";
import DeletePopUp from "components/DeletePopup";

function ReportDetail() {
  const lang: any = useBoundStore((state) => state.lang);

  const getAllReportConfigurations = useBoundStore(
    (state) => state.getAllReportConfigurations
  );

  const legalEntityNodeId = useBoundStore((state) => state.legalEntityNodeId);

  function getDateByRepititionInterval(date: any, repetitioninterval: any) {
    let formattedDate;
    // Extract time part from the date
    const timePart = moment(date).format("HH:mm");
    const dayOfMonth = moment(date).format("DD"); // Extract day of month

    switch (repetitioninterval) {
      case "1w":
        // For weekly repetition, include the extracted time
        formattedDate = `${i18n(lang).repition_weekly} (${moment(date).format(
          "dd"
        )}), ${timePart} Uhr`;
        break;
      case "1d":
        // For daily repetition, include the extracted time
        formattedDate = `${i18n(lang).repition_daily} , ${timePart} Uhr`;
        break;
      case "1m":
        // For monthly repetition, add 1 month to the date
        // Note: This will not change the time, so if you want to format it differently, adjust here
        formattedDate = `${
          i18n(lang).repition_monthly
        }  (${dayOfMonth}), ${timePart} Uhr`;

        break;
      default:
        // Default to just formatting the date if the interval isn't recognized
        formattedDate = moment(date).format("dddd, HH:mm");
    }

    return formattedDate;
  }

  const deleteReportConfiguration = useBoundStore(
    (state) => state.deleteReportConfiguration
  );
  const isAuthUser = useBoundStore((state) => state.isAuthUser);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const reportData = location.state?.report;
  const reportType = location.state?.reportType;
  let details = [{}];

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleCloseMore = () => {
    setAnchorEl(null);
  };

  const handleDeleteReportConfig = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setLoading(true);

    let equipmentsNodeIds: any = [];
    if (reportData.reportconfiguration?.hasequipment) {
      reportData.reportconfiguration?.hasequipment.map((eq: any) => {
        equipmentsNodeIds.push(eq.equipment.nodeid);
      });
    }
    let obj = {
      reportConfigurationNodeId: reportData.reportconfiguration?.nodeid,
      equipmentsNodeIds: equipmentsNodeIds ? equipmentsNodeIds : [""],
    };
    const promises = await deleteReportConfiguration(obj);
    if (isAuthUser === true) {
      try {
        const result = await Promise.all(promises);

        getAllReportConfigurations(legalEntityNodeId);
        navigate(reportType === "manual" ? "/manual-reports" : "/reports", {
          replace: true,
        });
      } catch (error) {
        getAllReportConfigurations(legalEntityNodeId);
        console.error("Error during promise execution:", error);
      }
    }
    setLoading(false);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const selectedLanguage = i18n(lang)?.language; // Get the selected language from your i18n function

  const getConcatenatedAreaNames = (areas: any, language: string) => {
    if (!areas || areas.length === 0) {
      return ""; // or any default value you prefer
    }
    if (areas.length === 1) {
      return `(${areas[0]?.area?.name[language]})`;
    }

    const areaNames = areas.map((area: any) => area.area.name[language]);
    return `(${areaNames.join(", ")})`;
  };

  const concatenatedAreaNames = getConcatenatedAreaNames(
    location?.state?.report?.reportconfiguration?.hasarea,
    selectedLanguage
  );

  const getConcatenatedEquipmentNames = (equipments: any, language: string) => {
    if (!equipments || equipments.length === 0) {
      return ""; // or any default value you prefer
    }

    if (equipments.length === 1) {
      return `(${equipments[0]?.equipment?.name[language]})`;
    }

    const equipmentNames = equipments.map(
      (equipment: any) => equipment.equipment.name[language]
    );
    return `(${equipmentNames.join(", ")})`;
  };

  const concatenatedEquipmentNames = getConcatenatedEquipmentNames(
    location?.state?.report?.reportconfiguration?.hasequipment,
    selectedLanguage
  );

  let nodeid = reportData?.reportconfiguration?.nodeid;
  const getAllReports = useBoundStore((state) => state.getAllReports);
  const getReports: any = useBoundStore((state) => state.getReports);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (nodeid) {
      getAllReports(nodeid);
    }
  }, [nodeid]);

  useEffect(() => {
    if (getReports && getReports.result) {
      // Check if result is an array with at least one element
      if (getReports.result.length > 0) {
        // Check if data is an array with at least one element
        if (getReports.result[0].data && getReports.result[0].data.length > 0) {
          setReports(
            getReports?.result[0]?.data[0]?.reportconfiguration?.hasreport
          );
        } else {
          console.error("Data array is empty or undefined");
        }
      } else {
        console.error("Result array is empty");
      }
    } else {
      console.error("getReports or result is undefined");
    }
  }, [getReports]);

  const [openDialogDelete, setopenDialogDelete] = useState(false);

  const handleDialogClose = () => {
    setopenDialogDelete(false);
  };

  const handleDeleteClick = () => {
    setopenDialogDelete(true);
  };

  return (
    <>
      <DeletePopUp
        open={openDialogDelete}
        handleClose={handleDialogClose}
        handleDelete={handleDeleteReportConfig}
      ></DeletePopUp>

      <div
        className="breadcrumpsWrapper"
        onClick={() => {
          navigate(reportType === "manual" ? "/manual-reports" : "/reports");
        }}
      >
        <div className="backIcon">
          <RoundedbackSvg />
        </div>
        <div className="breadcrumps">{i18n(lang).all_reports}</div>
      </div>
      <div className="bxShadow reportDetailCard">
        <Menu
          id="long-menu"
          MenuListProps={{
            "aria-labelledby": "long-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMore}
          className="moreActions"
        >
          <MenuItem id="1" onClick={handleCloseMore}>
            <Link
              to="/new-report"
              state={{
                report: reportData,
                isEditMode: true,
                activeId:
                  reportType === "time" ? 0 : reportType === "event" ? 1 : 2,
              }}
            >
              <div className="menuItem">
                <div className="icon" style={{ marginRight: "10px" }}>
                  <RoundedEditSvg />
                </div>
                <div className="text">
                  {" "}
                  {i18n(lang).report + " " + i18n(lang)?.edit}
                </div>
              </div>
            </Link>
          </MenuItem>
          <Divider orientation="horizontal" style={{ margin: 0 }} />
          <MenuItem id="1" onClick={handleDeleteClick}>
            <div className="menuItem">
              <div className="icon">
                <RoundedDeleteSvg />
              </div>
              <div className="text">
                {i18n(lang).report} {i18n(lang).delete}
              </div>
            </div>
          </MenuItem>
        </Menu>

        <div className="reportsHeader">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="reportTitle">
              <span>
                {" "}
                {getSvgByReportConfigType(location?.state?.reportType)}
              </span>{" "}
              <span>{location?.state?.report?.reportconfiguration?.title}</span>
            </div>
            <div className="reportActions">
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? "long-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </div>
          </div>

          <div className="iconsSet">
            <TextIcon
              icon={
                reportType === "time" || reportType === "" ? (
                  <RepititionInterval />
                ) : (
                  <FilledTimeSvg />
                )
              }
              iconText={
                reportType === "manual"
                  ? i18n(lang).manualReportsSelectOptions[
                      location?.state?.report?.reportconfiguration
                        .manualinterval
                    ].label
                  : getDateByRepititionInterval(
                      location?.state?.report?.reportconfiguration
                        ?.startingfrom,
                      location?.state?.report?.reportconfiguration
                        ?.repetitioninterval
                    )
              }
              className=""
            />

            <TextIcon
              icon={<VectorSvg />}
              iconText={
                location?.state?.report?.reportconfiguration?.allsites
                  ? "alle Werke"
                  : ` ${i18n(lang).sites} (${
                      Array.isArray(
                        location?.state?.report?.reportconfiguration?.hassite
                      ) &&
                      location?.state?.report?.reportconfiguration?.hassite
                        .length > 0
                        ? location?.state?.report?.reportconfiguration?.hassite
                            .length
                        : "0"
                    })`
              }
              className=""
            />
            <TextIcon
              icon={<SpaceDashboardSvg />}
              iconText={location?.state?.areas}
              className=""
            />
            <TextIcon
              icon={<OvenGenSvg />}
              iconText={location?.state?.equipments}
              className=""
            />
            <TextIcon
              icon={
                reportType === "time" || reportType === "" ? (
                  <SystemUpdateSvg />
                ) : reportType === "manual" ? (
                  <ProductFilter />
                ) : (
                  ""
                )
              }
              iconText={
                reportType === "time" || reportType === ""
                  ? location?.state?.exportSettings
                  : location?.state?.report?.reportconfiguration
                      ?.filterbyproduct
              }
              className=""
            />
          </div>
        </div>
        <Divider orientation="horizontal" />
        <div style={{ padding: "0px 24px 30px 24px" }}>
          {reports.length ? (
            <ReportsTable report={reportData} reportType={reportType} />
          ) : (
            <EmptyState
              style={{ paddingBottom: "60px !important" }}
              title={i18n(lang)?.emptm_state_report_detail_title}
              description={i18n(lang)?.emptm_state_report_detail_description}
              icon={<ChartSvg />}
              hasButton
              button={
                <div style={{ marginTop: "20px" }}>
                  <Link
                    to={"/new-report"}
                    state={{
                      report: reportData,
                      isEditMode: true,
                      activeId:
                        reportType === "time"
                          ? 0
                          : reportType === "event"
                          ? 1
                          : 2,
                    }}
                  >
                    <Button
                      startIcon={<EditSvg />}
                      variant="outlined"
                      sx={{
                        backgroundColor: "var(--primary-color)",
                        letterSpacing: "1.25px",
                      }}
                    >
                      {i18n(lang)?.report_edit}
                    </Button>
                  </Link>
                </div>
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

export default ReportDetail;
